// frontend/src/components/reports/ReportSkeleton.tsx
//
// Esqueleto de la tabla mientras carga.
//
// Sustituye al spinner por un motivo concreto: las vistas de Fabric tardan de 6 a
// 50 s según la carga, así que la espera se ve siempre. Un spinner sobre un hueco
// vacío da sensación de página en blanco; un esqueleto con la forma de la tabla
// dice «esto va a ser una tabla y está llegando».
//
// Ocupa el MISMO hueco que la tabla que va a sustituir: mismo alto acotado, misma
// canaleta y una barra por columna con su ancho real. Antes era un bloque de nueve
// filas con anchos inventados y tope de ocho columnas, así que dejaba media
// rejilla vacía y al llegar el dato la página daba un salto.
//
// Es una rejilla plana, sin un elemento por fila: envolver cada fila obligaría a
// `subgrid` para heredar las columnas, y no hace falta pagar ese requisito de
// compatibilidad para pintar unas barras.
import { Fragment, useMemo } from 'react'
import type { ReportCol } from './reportDefs'
import { GUTTER_W } from './tableLayout'

/** Alto de fila, alineado con el `py-2` de las celdas reales. */
const ALTO_FILA = 33

/** Ancho por defecto de una columna que no declara `minW`, igual que en la tabla. */
const ANCHO_POR_DEFECTO = 120

/**
 * Filas pintadas. Se pasa de largo a propósito y el contenedor recorta: así llena
 * la altura en cualquier pantalla sin medir el hueco ni observar su tamaño.
 */
const FILAS = 30

interface Props {
  dark: boolean
  /** Las columnas visibles, para reproducir la rejilla real. */
  cols: ReportCol[]
}

export function ReportSkeleton({ dark, cols }: Props) {
  const barra = dark ? 'bg-white/10' : 'bg-slate-200'
  const barraCabecera = dark ? 'bg-white/15' : 'bg-slate-300'
  const cebraBg = dark ? 'bg-white/[0.02]' : 'bg-slate-50/60'

  // Rejilla idéntica a la de la tabla: canaleta fija más el ancho de cada columna.
  // La última se estira, para que no quede una franja muerta a la derecha cuando
  // las columnas no llegan a llenar el contenedor.
  const plantilla = useMemo(() => {
    const anchos = cols.map(c => `${c.minW ?? ANCHO_POR_DEFECTO}px`)
    if (anchos.length > 0) {
      anchos[anchos.length - 1] = `minmax(${anchos[anchos.length - 1]}, 1fr)`
    }
    return [`${GUTTER_W}px`, ...anchos].join(' ')
  }, [cols])

  /** Celdas de una fila: la canaleta y una barra por columna. */
  function fila(i: number) {
    // Se desvanece hacia abajo, con suelo: sin él las últimas filas se volvían
    // invisibles y el esqueleto volvía a parecer un hueco a medio llenar.
    const estilo = {
      height: ALTO_FILA,
      opacity: Math.max(0.2, 1 - i * 0.045),
    }
    const cebra = i % 2 === 1 ? cebraBg : ''
    return (
      <Fragment key={i}>
        <div className={`px-3 flex items-center animate-pulse ${cebra}`} style={estilo}>
          <div className={`h-2.5 w-2.5 rounded ${barra}`} />
        </div>
        {cols.map((c, col) => (
          <div
            key={c.key}
            className={`px-3 flex items-center animate-pulse ${cebra}`}
            style={estilo}
          >
            {/* El ancho varía con fila y columna para que no se lea como un patrón
                repetido, pero de forma determinista: con `Math.random()` cada
                repintado movería las barras y el bloque parecería vibrar. */}
            <div
              className={`h-2.5 rounded ${barra}`}
              style={{ width: `${45 + ((i * 7 + col * 23) % 45)}%` }}
            />
          </div>
        ))}
      </Fragment>
    )
  }

  return (
    <div
      // El mismo hueco acotado que `ReportTable`, para que al llegar el dato la
      // tabla ocupe exactamente donde estaba el esqueleto.
      className="overflow-hidden max-h-[calc(100vh-21rem)] min-h-[16rem]"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="grid" style={{ gridTemplateColumns: plantilla }}>
        {/* Cabecera: más marcada, como en la tabla. */}
        <div className="px-3 py-2.5" />
        {cols.map(c => (
          <div key={c.key} className="px-3 py-2.5">
            <div className={`h-2.5 w-[70%] rounded ${barraCabecera}`} />
          </div>
        ))}

        {Array.from({ length: FILAS }, (_, i) => fila(i))}
      </div>
    </div>
  )
}
